import { useCallback, useEffect, useState } from 'react'

import { CursorState } from '@editor/config/constants/cursor'
import { BlueprintEditorTools } from '@editor/pages/projects/blueprint-editor/definitions'
import { RootState, useAppDispatch } from '@editor/store/app'
import { setCursor } from '@editor/store/tool'
import { ThreeEvent } from '@react-three/fiber'
import { useSelector } from 'react-redux'

import { AnchorsProps } from '../definitions'

const useAnchorEventHandlers = ({ isEditing, editable, onDown }: AnchorsProps) => {
  // Store
  const dispatch = useAppDispatch()
  const selectedTool = useSelector((state: RootState) => state.blueprint.editor.selectedTool)

  // States
  const [hoveredAnchor, setHoveredAnchorId] = useState<number | null>(null)

  // Dervied
  const isSelectedTool = selectedTool === BlueprintEditorTools.MeasurementLabel

  /**
   * Handle mouse down events on the anchor.
   * Most logic is handled in parent component. onDown is called to notify parent
   * the start of a drag operation.
   *
   * @param anchor The index of the anchor.
   * @returns A mouse down event handler.
   */
  const mouseDownHandler = useCallback(
    (anchor: number) => (ev: ThreeEvent<MouseEvent>) => {
      if (!editable) return

      ev.stopPropagation()

      dispatch(setCursor(CursorState.GRABBING))
      onDown?.(anchor)
    },
    [editable, onDown, dispatch],
  )

  /**
   * Handle mouse enter events on the anchor.
   *
   * @param ev The mouse event.
   */
  const mouseEnterHandler = useCallback(
    (anchor: number) => (ev: ThreeEvent<MouseEvent>) => {
      ev.stopPropagation()

      dispatch(setCursor(CursorState.GRAB))
      setHoveredAnchorId(anchor)
    },
    [dispatch],
  )

  /**
   * Handle mouse leave events on the anchor.
   * Only resets the cursor if we are not editing an anchor.
   *
   * Reset for while editing is handled in tool.
   *
   * @param ev The mouse event.
   */
  const mouseLeaveHandler = useCallback(
    (ev: ThreeEvent<MouseEvent>) => {
      if (isEditing) return

      ev.stopPropagation()
      dispatch(setCursor(isSelectedTool ? CursorState.CROSSHAIR : CursorState.DEFAULT))
      setHoveredAnchorId(null)
    },
    [isEditing, isSelectedTool, dispatch],
  )

  /**
   * Mouse up needs to be handled on window level to ensure it works even when
   * the mouse is outside annotation box, or even worse, outside the canvas.
   */
  useEffect(() => {
    /**
     * Handle mouse up events on the anchor.
     * This is meant to be bound to window or document to ensure capturing the event.
     *
     * @param anchor The index of the anchor.
     * @returns A mouse up event handler.
     */
    const handleMouseUp = (ev: MouseEvent) => {
      ev.stopPropagation()
      setHoveredAnchorId(null)
    }

    window.addEventListener('mouseup', handleMouseUp)
    return () => {
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [editable, dispatch])

  return {
    hoveredAnchor,
    mouseDownHandler,
    mouseEnterHandler,
    mouseLeaveHandler,
  }
}

export default useAnchorEventHandlers
